import LockIcon from "@mui/icons-material/Lock";
import {
  Alert,
  Button,
  IconButton,
  type AlertProps,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  SnackbarContent,
  type CustomContentProps,
  closeSnackbar,
} from "notistack";
import { forwardRef } from "react";

import {
  CheckCircleIcon,
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  WarningAmberIcon,
} from "@/icons";

export type OrionSnackbarSeverity =
  | "error"
  | "warning"
  | "info"
  | "success"
  | "restricted";

const iconMapping = {
  error: <ErrorIcon color="error" />,
  warning: <WarningAmberIcon color="warning" />,
  info: <InfoIcon color="info" />,
  success: <CheckCircleIcon color="success" />,
};

/**
 * "restricted" severity: a black notification with a lock icon and white text,
 * for capabilities the site hasn't been granted/activated yet. MUI's `Alert`
 * has no such severity, so this preset styles the base Alert instead.
 */
export function RestrictedAlert({ sx, ...props }: AlertProps) {
  const baseSx: SxProps<Theme> = {
    bgcolor: "common.black",
    color: "common.white",
    "& .MuiAlert-icon": { color: "common.white" },
    "& .MuiAlertTitle-root": { color: "common.white" },
  };
  return (
    <Alert
      icon={<LockIcon fontSize="inherit" />}
      sx={[baseSx, ...((Array.isArray(sx) ? sx : [sx]) as SxProps<Theme>[])]}
      {...props}
    />
  );
}

export interface OrionSnackbarProps extends CustomContentProps {
  severity?: OrionSnackbarSeverity;
  showCloseButton?: boolean;
  onRetry?: () => void;
}

export const OrionSnackbar = forwardRef<HTMLDivElement, OrionSnackbarProps>(
  (
    {
      id,
      className,
      style,
      message,
      variant,
      severity,
      hideIconVariant,
      action,
      showCloseButton = true,
      onRetry,
    },
    ref,
  ) => {
    // Use variant directly, fallback to severity, then default to info
    const alertSeverity =
      (variant as OrionSnackbarSeverity) || severity || "info";

    // Build retry button if callback is provided
    const retryButton = onRetry ? (
      <Button
        size="small"
        color="inherit"
        onClick={() => {
          onRetry();
          closeSnackbar(id);
        }}
      >
        Retry
      </Button>
    ) : null;

    // Build close button if needed
    const closeButton = showCloseButton ? (
      <IconButton
        size="small"
        color="inherit"
        onClick={() => closeSnackbar(id)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    ) : null;

    // Build action section: retry button, custom actions, then close button
    const alertAction = (
      <>
        {retryButton}
        {action && (typeof action === "function" ? action(id) : action)}
        {closeButton}
      </>
    );

    return (
      <SnackbarContent
        ref={ref}
        role="alert"
        style={style}
        className={className}
        id={id?.toString()}
      >
        {alertSeverity === "restricted" ? (
          <RestrictedAlert
            {...(hideIconVariant === true ? { icon: false } : {})}
            action={alertAction}
            sx={{ width: "100%", "& .MuiAlert-message": { width: "100%" } }}
          >
            {message}
          </RestrictedAlert>
        ) : (
          <Alert
            severity={alertSeverity}
            iconMapping={iconMapping}
            {...(hideIconVariant === true ? { icon: false } : {})}
            action={alertAction}
            sx={{
              width: "100%",
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            {message}
          </Alert>
        )}
      </SnackbarContent>
    );
  },
);

OrionSnackbar.displayName = "OrionSnackbar";
